/**
 * Normalized server validation error for form fields and errors drawer.
 */
export type ServerFormError = {
  path: string;
  fieldLabel?: string;
  message: string;
};

/**
 * Error thrown from onAction to show FormErrorsDrawer instead of snackbar.
 * Use when validation fails inside onAction (e.g. custom business rules).
 */
export class ValidationErrorsError extends Error {
  validationErrors: ServerFormError[];

  constructor(validationErrors: ServerFormError[]) {
    const first = validationErrors[0];
    super(first?.message ?? 'Validation failed');
    this.name = 'ValidationErrorsError';
    this.validationErrors = validationErrors;
  }
}

/**
 * Checks if the error is a GraphQL/Genql error with an errors array.
 */
export function isServerFormErrorsError(error: unknown): error is { errors: Array<Record<string, unknown>> } {
  return (
    typeof error === 'object' &&
    error !== null &&
    'errors' in error &&
    Array.isArray((error as { errors: unknown }).errors)
  );
}

/**
 * Parse form field path from GraphQL error message (e.g. at "v2.items[0].components[0]" -> "items.0.components.0").
 * Used when path is not in error.path or extensions.path (e.g. variable validation errors).
 */
function parseFormPathFromMessage(message: string): string {
  const match = String(message).match(/\bat\s+["']([^"']+)["']/);
  if (!match) return '';
  const raw = match[1].replace(/\[\d+\]/g, (s) => `.${s.slice(1, -1)}`);
  const segments = raw.split('.').filter(Boolean);
  if (segments.length <= 1) return '';
  return segments.slice(1).join('.');
}

/**
 * Normalize GraphQL errors to { path, fieldLabel?, message }.
 * Path: from extensions.path (string), path array (skip first segment), or parsed from message.
 */
export function normalizeServerFormErrors(errors: Array<Record<string, unknown>>): ServerFormError[] {
  return errors.map((err) => {
    const pathArr = err.path as Array<string | number> | undefined;
    const ext = (err.extensions as Record<string, unknown> | undefined) ?? {};
    let pathStr =
      typeof ext.path === 'string'
        ? ext.path
        : Array.isArray(pathArr) && pathArr.length > 0
          ? pathArr
              .filter((_, i) => i > 0)
              .map(String)
              .join('.')
          : '';
    if (!pathStr && typeof err.message === 'string') {
      pathStr = parseFormPathFromMessage(err.message);
    }
    const fieldLabel = typeof ext.fieldLabel === 'string' ? ext.fieldLabel : undefined;
    const message = typeof err.message === 'string' ? err.message : 'Validation error';
    return {
      path: pathStr || 'root',
      fieldLabel,
      message,
    };
  });
}
